<template>
    <generic-page>
        <div v-if="creator">
            <b-btn
                variant="link"
                size="lg"
                class="pl-0"
                to="/affiliate/creator/index"
            >
                <font-awesome-icon size="lg" icon="fa-light fa-chevron-left" />
                Creators
            </b-btn>

            <div class="d-flex align-items-center justify-content-between my-2">
                <div class="d-flex align-items-center">
                    <img
                        class="rounded mr-2"
                        :src="creator.standardMap.photo"
                    />
                    <h5 class="mb-0">
                        {{ creator.standardMap.name }}
                    </h5>
                </div>
                <div>
                    <coin-badge
                        class="bg-white"
                        :amount="creator.balance"
                        label="Current Balance"
                    />
                </div>
            </div>

            <b-list-group>
                <b-list-group-item
                    class="d-flex align-items-center justify-content-between"
                >
                    Last Active

                    <div class="d-flex align-items-center">
                        {{ creator.standardMap.lastActiveRelative }}
                    </div>
                </b-list-group-item>
                <b-list-group-item
                    class="d-flex align-items-center justify-content-between"
                >
                    Created
                    <div class="d-flex align-items-center">
                        {{ creator.standardMap.createdRelative }}
                    </div>
                </b-list-group-item>
                <b-list-group-item
                    class="d-flex align-items-center justify-content-between"
                >
                    Location
                    <div class="d-flex align-items-center">
                        <img class="mr-1" :src="creator.standardMap.flagUrl" />
                        {{ creator.standardMap.country }}
                    </div>
                </b-list-group-item>
                <b-list-group-item
                    class="d-flex align-items-center justify-content-between"
                    v-if="creator.creditRefillTotal"
                >
                    Total Spend
                    <div>${{ creator.creditRefillTotal }}</div>
                </b-list-group-item>
                <b-list-group-item
                    class="d-flex align-items-center justify-content-between"
                    v-if="creator.source"
                >
                    Source
                    <div>
                        {{ creator.source }}
                    </div>
                </b-list-group-item>
                <b-list-group-item
                    class="d-flex align-items-center justify-content-between"
                    v-if="creator.landing"
                >
                    Landing Page
                    <div>
                        {{ creator.landing }}
                    </div>
                </b-list-group-item>
                <b-list-group-item
                    class="d-flex align-items-center justify-content-between"
                    v-if="creator.daysActive"
                >
                    Days Active
                    <div>
                        {{ creator.daysActive }}
                    </div>
                </b-list-group-item>
                <b-list-group-item
                    class="d-flex align-items-center justify-content-between"
                    v-if="creator.activityCount"
                >
                    Activity Count
                    <div>
                        {{ creator.activityCount }}
                    </div>
                </b-list-group-item>
            </b-list-group>

            <b-card class="mt-3">
                <div class="d-flex align-items-center justify-content-between">
                    <h5 class="mb-0">
                        Elite Status
                        <b-badge
                            v-if="creator.elite"
                            variant="primary"
                            class="ml-1"
                        >
                            Elite
                        </b-badge>
                        <b-badge v-else variant="dark" class="ml-1">
                            Not Elite
                        </b-badge>
                    </h5>
                    <b-btn
                        v-if="creator.elite"
                        variant="outline-danger"
                        size="sm"
                        :disabled="eliteLoading"
                        @click="disableElite"
                    >
                        Remove Elite
                    </b-btn>
                </div>

                <div v-if="!creator.elite" class="mt-3">
                    <b-btn
                        v-if="!showEliteWarning"
                        variant="primary"
                        :disabled="eliteLoading"
                        @click="showEliteWarning = true"
                    >
                        Promote to Elite
                    </b-btn>

                    <b-alert
                        v-if="showEliteWarning"
                        variant="warning"
                        show
                        class="mb-0"
                    >
                        Promoting to Elite will create a public landing page
                        for this creator.
                        <div class="mt-2">
                            <b-btn
                                variant="light"
                                size="sm"
                                :disabled="eliteLoading"
                                @click="showEliteWarning = false"
                            >
                                Cancel
                            </b-btn>
                            <b-btn
                                variant="primary"
                                size="sm"
                                class="ml-1"
                                :disabled="eliteLoading"
                                @click="enableElite"
                            >
                                Confirm Promotion
                            </b-btn>
                        </div>
                    </b-alert>
                </div>

                <template v-if="creator.elite">
                    <h6 class="mt-3">
                        Username
                        <span v-if="!creator.slug" class="text-danger">*</span>
                        <b-btn
                            variant="link"
                            size="sm"
                            class="p-0 ml-1"
                            @click="showUsernameHelp = true"
                        >
                            <font-awesome-icon
                                icon="fa-light fa-circle-info"
                            />
                        </b-btn>
                    </h6>

                    <div v-if="!creator.slug" class="text-muted small mb-2">
                        Required for their landing page to go live
                    </div>

                    <div v-if="creator.slug">
                        <div class="d-flex align-items-center">
                            <font-awesome-icon
                                icon="fa-light fa-link"
                                class="text-muted mr-2"
                            />
                            <span class="font-weight-bold">
                                {{ creator.slug }}
                            </span>
                        </div>
                    </div>

                    <div v-else>
                        <b-input-group>
                            <b-form-input
                                v-model="slugInput"
                                placeholder="e.g. Molly123"
                                maxlength="16"
                                :state="slugError ? false : null"
                                :disabled="slugLoading"
                            />
                            <b-input-group-append>
                                <b-btn
                                    variant="primary"
                                    :disabled="slugLoading || !slugInput"
                                    @click="setSlug"
                                >
                                    Save
                                </b-btn>
                            </b-input-group-append>
                        </b-input-group>
                        <b-form-invalid-feedback
                            :state="slugError ? false : null"
                        >
                            {{ slugError }}
                        </b-form-invalid-feedback>
                    </div>

                    <h6 class="mt-3">Interview</h6>

                    <div v-if="!creator.interview || interviewEditing">
                        <b-input-group>
                            <b-form-input
                                v-model="interviewUrl"
                                placeholder="YouTube video URL"
                                :state="interviewError ? false : null"
                                :disabled="interviewLoading"
                            />
                            <b-input-group-append>
                                <b-btn
                                    variant="primary"
                                    :disabled="
                                        interviewLoading || !interviewUrl
                                    "
                                    @click="setInterview"
                                >
                                    Save
                                </b-btn>
                                <b-btn
                                    v-if="interviewEditing"
                                    variant="light"
                                    :disabled="interviewLoading"
                                    @click="cancelInterviewEdit"
                                >
                                    Cancel
                                </b-btn>
                            </b-input-group-append>
                        </b-input-group>
                        <b-form-invalid-feedback
                            :state="interviewError ? false : null"
                        >
                            {{ interviewError }}
                        </b-form-invalid-feedback>
                    </div>

                    <div v-else>
                        <div
                            class="embed-responsive embed-responsive-16by9 mb-2"
                        >
                            <iframe
                                class="embed-responsive-item"
                                :src="creator.interview.embedUrl"
                                allowfullscreen
                            ></iframe>
                        </div>
                        <div
                            class="d-flex align-items-center justify-content-between"
                        >
                            <a
                                :href="creator.interview.url"
                                target="_blank"
                                class="text-truncate mr-2"
                            >
                                {{ creator.interview.url }}
                            </a>
                            <b-dropdown
                                variant="secondary"
                                size="sm"
                                right
                                no-caret
                            >
                                <template #button-content>
                                    <font-awesome-icon
                                        icon="fa-light fa-ellipsis"
                                    />
                                </template>
                                <b-dropdown-item @click="editInterview">
                                    Edit
                                </b-dropdown-item>
                                <b-dropdown-item
                                    :disabled="interviewLoading"
                                    @click="deleteInterview"
                                >
                                    Delete
                                </b-dropdown-item>
                            </b-dropdown>
                        </div>
                    </div>

                    <h6 class="mt-3">Rebuild Site</h6>

                    <div
                        class="d-flex align-items-center justify-content-between"
                    >
                        <div>
                            <b-btn
                                variant="primary"
                                :disabled="
                                    buildLoading || !affiliate?.build?.canBuild
                                "
                                @click="triggerBuild"
                            >
                                Rebuild Site
                            </b-btn>
                        </div>
                        <div
                            v-if="
                                affiliate?.build &&
                                !affiliate.build.canBuild
                            "
                            class="text-muted small"
                        >
                            Available in {{ buildCountdownDisplay }}
                        </div>
                    </div>

                    <div v-if="buildError" class="text-danger small mt-1">
                        {{ buildError }}
                    </div>
                </template>

                <b-modal
                    v-model="showUsernameHelp"
                    title="About Usernames"
                    ok-only
                    ok-title="Got it"
                    centered
                >
                    <p>
                        The username is used in the URL of this creator's
                        landing page. It won't appear anywhere on the page
                        itself, but it will be visible in the URLs you promote
                        on YouTube and other channels.
                    </p>
                    <p>
                        Choose something that looks organic, like a real
                        username. Only letters, numbers, dashes and underscores
                        are allowed, up to 16 characters.
                    </p>
                    <p>
                        Usernames are unique — if the one you want is already
                        taken, try adding a number or variation.
                    </p>
                    <p class="font-weight-bold mb-0">
                        Once a username is set, it cannot be changed to prevent
                        breaking existing links. If a URL absolutely needs to be
                        updated, contact us and we can manually update it.
                    </p>
                </b-modal>
            </b-card>

            <h5 class="mt-3">Commission History</h5>
            <b-table-simple striped class="mb-0 mt-3">
                <thead>
                    <th>Date</th>
                    <th>Commission</th>
                </thead>
                <tbody>
                    <tr
                        :key="inviteReward.id"
                        v-for="inviteReward in creator.inviteRewards"
                    >
                        <td>
                            {{ inviteReward.createdRelative }}
                        </td>
                        <td>{{ inviteReward.commissionLocal }}</td>
                    </tr>
                    <tr>
                        <td class="font-weight-bold bg-white">Total</td>
                        <td class="font-weight-bold bg-white">
                            {{ creator.totalRewards }}
                        </td>
                    </tr>
                </tbody>
            </b-table-simple>
        </div>
        <div v-else>
            <generic-loading />
        </div>
    </generic-page>
</template>

<script>
export default {
    data() {
        return {
            creator: null,
            affiliate: null,
            eliteLoading: false,
            showEliteWarning: false,
            interviewUrl: "",
            interviewEditing: false,
            interviewLoading: false,
            interviewError: null,
            slugInput: "",
            slugLoading: false,
            slugError: null,
            showUsernameHelp: false,
            buildLoading: false,
            buildError: null,
            buildCountdownSeconds: 0,
            buildCountdownTimer: null,
        };
    },
    computed: {
        buildCountdownDisplay() {
            const total = this.buildCountdownSeconds;
            const hours = Math.floor(total / 3600);
            const minutes = Math.floor((total % 3600) / 60);
            if (hours > 0) {
                return `${hours}h ${minutes}m`;
            }
            return `${minutes}m`;
        },
    },
    created() {
        if (this.$route.params?.id) {
            this.refresh();
        }
    },
    beforeDestroy() {
        clearInterval(this.buildCountdownTimer);
    },
    methods: {
        refresh() {
            this.api
                .post(`/affiliate/creator/${this.$route.params?.id}/get`)
                .then((response) => {
                    if (response?.data?.creator) {
                        this.creator = response.data.creator;
                    }
                    if (response?.data?.affiliate) {
                        this.affiliate = response.data.affiliate;
                        this.startBuildCountdown();
                    }
                });
        },
        startBuildCountdown() {
            clearInterval(this.buildCountdownTimer);
            this.buildCountdownSeconds =
                this.affiliate?.build?.secondsUntilNextBuild || 0;
            if (this.buildCountdownSeconds > 0) {
                this.buildCountdownTimer = setInterval(() => {
                    this.buildCountdownSeconds--;
                    if (this.buildCountdownSeconds <= 0) {
                        this.buildCountdownSeconds = 0;
                        clearInterval(this.buildCountdownTimer);
                        this.refresh();
                    }
                }, 1000);
            }
        },
        enableElite() {
            this.eliteLoading = true;
            this.api
                .post(
                    `/affiliate/creator/${this.$route.params?.id}/enableElite`
                )
                .then(() => {
                    this.showEliteWarning = false;
                    this.refresh();
                })
                .finally(() => {
                    this.eliteLoading = false;
                });
        },
        disableElite() {
            this.eliteLoading = true;
            this.api
                .post(
                    `/affiliate/creator/${this.$route.params?.id}/disableElite`
                )
                .then(() => {
                    this.refresh();
                })
                .finally(() => {
                    this.eliteLoading = false;
                });
        },
        setInterview() {
            this.interviewLoading = true;
            this.interviewError = null;
            this.api
                .post(
                    `/affiliate/creator/${this.$route.params?.id}/setInterview`,
                    { url: this.interviewUrl }
                )
                .then(() => {
                    this.interviewUrl = "";
                    this.interviewEditing = false;
                    this.refresh();
                })
                .catch((error) => {
                    if (error?.response?.status === 422) {
                        const errors = error.response.data?.errors;
                        if (errors?.url) {
                            this.interviewError = errors.url[0];
                        }
                    }
                })
                .finally(() => {
                    this.interviewLoading = false;
                });
        },
        deleteInterview() {
            this.interviewLoading = true;
            this.api
                .post(
                    `/affiliate/creator/${this.$route.params?.id}/deleteInterview`
                )
                .then(() => {
                    this.interviewUrl = "";
                    this.refresh();
                })
                .finally(() => {
                    this.interviewLoading = false;
                });
        },
        editInterview() {
            this.interviewUrl = this.creator.interview.url;
            this.interviewEditing = true;
            this.interviewError = null;
        },
        cancelInterviewEdit() {
            this.interviewEditing = false;
            this.interviewUrl = "";
            this.interviewError = null;
        },
        triggerBuild() {
            this.buildLoading = true;
            this.buildError = null;
            this.api
                .post("/affiliate/creator/triggerBuild")
                .then(() => {
                    this.refresh();
                })
                .catch((error) => {
                    if (error?.response?.status === 422) {
                        const errors = error.response.data?.errors;
                        if (errors?.build) {
                            this.buildError = errors.build[0];
                        }
                    }
                })
                .finally(() => {
                    this.buildLoading = false;
                });
        },
        setSlug() {
            this.slugLoading = true;
            this.slugError = null;
            this.api
                .post(
                    `/affiliate/creator/${this.$route.params?.id}/setSlug`,
                    { slug: this.slugInput }
                )
                .then(() => {
                    this.slugInput = "";
                    this.refresh();
                })
                .catch((error) => {
                    if (error?.response?.status === 422) {
                        const errors = error.response.data?.errors;
                        if (errors?.slug) {
                            this.slugError = errors.slug[0];
                        } else if (error.response.data?.message) {
                            this.slugError = error.response.data.message;
                        }
                    }
                })
                .finally(() => {
                    this.slugLoading = false;
                });
        },
    },
    components: {},
};
</script>